import { Link } from "@simplybusiness/mobius";
import type {
  TradeOption,
  TradeSelectorProps,
  TradeSelectorVariant,
  TradeSelectionMeta,
} from "@simplybusiness/mobius-trade-selector";
import { TradeSelector } from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext } from "@simplybusiness/services";
import { useEffect, useState } from "react";
import { useEnvironmentValues } from "../../../../../../../hooks/environment";
import { useAnswer } from "../../../../../../../features/questionnaire";
import { HelpLink } from "../../../HelpLink";
import { QuestionLabel } from "../../../QuestionLabel";
import type { QuestionComponentProps } from "../../types";
import { redirect } from "./redirect";
import { TradeQuestionSelected } from "./TradeQuestionSelected";
import { EMPTY_TRADE_OPTION, getTradeOptionFromAnswer } from "./utils";

export const TradeQuestion = ({
  questionFieldProps,
  config,
  type,
}: QuestionComponentProps) => {
  const {
    onChange,
    name,
    label,
    errorMessage,
    questionLabelProps,
    helpInfo,
    ...otherQuestionProps
  } = questionFieldProps;
  const {
    occupationsPageUrl,
    allowTradeBasedRedirect,
    ...tradeSelectorConfig
  } = config || {};
  const answerValue = useAnswer(name);
  const answeredTrade = answerValue?.toString() ?? "";

  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext();

  const [selectedTrade, setSelectedTrade] = useState<TradeOption>(
    getTradeOptionFromAnswer(answeredTrade),
  );

  // Keep selectedTrade in sync with answeredTrade
  useEffect(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(getTradeOptionFromAnswer(answeredTrade));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredTrade]);

  const variant: TradeSelectorVariant =
    type === "trade_search_ai" ? "ai" : "default";

  const handleSelectTrade = (trade: TradeOption, meta?: TradeSelectionMeta) => {
    onChange?.({
      target: {
        name,
        value: trade.value,
        tradeSelectionMeta: meta,
      },
    });
    setSelectedTrade(trade);
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      verticalsWithoutRedirect: trade.verticalsWithoutRedirect,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(allowTradeBasedRedirect),
    });
  };

  const handleTradeChangeClick = () => {
    // Send snowplow event for changing trade
    snowplow?.trigger("tradeChanged", {
      label: questionFieldProps.id,
      property: answeredTrade,
    });
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };

  return (
    <div className="mobius-trade-question">
      {selectedTrade.value ? (
        <TradeQuestionSelected
          trade={selectedTrade}
          onEditTrade={handleTradeChangeClick}
          primaryText={questionLabelProps.primaryText}
        />
      ) : (
        <>
          <QuestionLabel {...questionLabelProps} />
          {helpInfo && <HelpLink {...helpInfo} />}
          <TradeSelector
            defaultValue={answeredTrade}
            onSelectTrade={handleSelectTrade}
            variant={variant}
            {...(tradeSelectorConfig as unknown as TradeSelectorProps)}
            site={site}
            vertical={vertical!}
            {...otherQuestionProps}
          />
          {typeof occupationsPageUrl === "string" && (
            <div className="mobius-trade-question__note">
              Or choose from the{" "}
              <Link href={occupationsPageUrl}>full list of occupations</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};
